import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Pin, Plus, MessageSquare, X } from '@tamagui/lucide-icons'
import { useState, useMemo } from 'react'
import { forumPosts, users } from '@/mocks'
import { Avatar } from '../../common'

const Container = styled(View, {
  flex: 1,
  flexDirection: 'column',
})

const ForumHeader = styled(View, {
  height: 48,
  paddingHorizontal: 16,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const CreateButton = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  height: 32,
  paddingHorizontal: 12,
  borderRadius: '$borderRadiusMd',
  backgroundColor: '$accent',
  cursor: 'pointer',
  hoverStyle: { opacity: 0.9 },
})

const PostCard = styled(View, {
  padding: 16,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  gap: 10,
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const Tag = styled(View, {
  paddingHorizontal: 8,
  paddingVertical: 2,
  borderRadius: '$borderRadiusSm',
  backgroundColor: '$sidebarActive',
})

const CreateForm = styled(View, {
  padding: 16,
  backgroundColor: '$sidebar',
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  gap: 12,
})

const SubmitButton = styled(View, {
  height: 32,
  paddingHorizontal: 16,
  borderRadius: '$borderRadiusMd',
  backgroundColor: '$accent',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  hoverStyle: { opacity: 0.9 },
})

const tagColors: Record<string, string> = {
  'feature-request': '#3ECFCF',
  'bug': '#CF3E4F',
  'feedback': '#E8A840',
  'question': '#3B82F6',
  'discussion': '#A855F7',
  'announcement': '#3ECF7B',
  'roadmap': '#3ECF7B',
  'ui': '#EC4899',
  'voice': '#E8A840',
  'mobile': '#3B82F6',
  'themes': '#A855F7',
  'messages': '#3ECFCF',
  'bots': '#8B95A8',
  'linux': '#E8A840',
  'privacy': '#3ECF7B',
}

function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString()
}

interface ForumViewProps {
  channelId: string
}

export function ForumView({ channelId }: ForumViewProps) {
  const theme = useTheme()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newBody, setNewBody] = useState('')
  const [newTags, setNewTags] = useState<string[]>([])

  const posts = forumPosts[channelId] || []

  const sortedPosts = useMemo(() => {
    const pinned = posts.filter(p => p.isPinned)
    const unpinned = posts.filter(p => !p.isPinned)
    return [...pinned, ...unpinned]
  }, [posts])

  const availableTags = useMemo(() => {
    const tags = new Set<string>()
    posts.forEach(p => p.tags.forEach(t => tags.add(t)))
    return Array.from(tags)
  }, [posts])

  const toggleTag = (tag: string) => {
    setNewTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])
  }

  const handleCreate = () => {
    setShowCreateForm(false)
    setNewTitle('')
    setNewBody('')
    setNewTags([])
  }

  return (
    <Container>
      <ForumHeader>
        <Text fontFamily="$fontHeading" fontWeight="600" fontSize={15} color="$surfaceForeground">
          Forum
        </Text>
        <CreateButton onPress={() => setShowCreateForm(!showCreateForm)}>
          {showCreateForm ? (
            <X size={14} color="$surface" />
          ) : (
            <Plus size={14} color="$surface" />
          )}
          <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">
            {showCreateForm ? 'Cancel' : 'New Post'}
          </Text>
        </CreateButton>
      </ForumHeader>

      {showCreateForm && (
        <CreateForm>
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Post title"
            style={{
              background: 'transparent',
              border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
              borderRadius: 6, padding: '8px 12px',
              color: theme.surfaceForeground?.val || '#E2E8F0',
              fontFamily: 'inherit', fontSize: 14,
              outline: 'none', width: '100%', boxSizing: 'border-box',
            }}
          />
          <textarea
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            placeholder="Write your post..."
            rows={4}
            style={{
              background: 'transparent',
              border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
              borderRadius: 6, padding: '8px 12px',
              color: theme.surfaceForeground?.val || '#E2E8F0',
              fontFamily: 'inherit', fontSize: 14,
              outline: 'none', resize: 'none', width: '100%', boxSizing: 'border-box',
            }}
          />
          <View gap={4}>
            <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">Tags</Text>
            <View flexDirection="row" gap={6} flexWrap="wrap">
              {availableTags.map((tag) => (
                <Tag
                  key={tag}
                  onPress={() => toggleTag(tag)}
                  cursor="pointer"
                  borderWidth={newTags.includes(tag) ? 1 : 0}
                  borderColor={tagColors[tag] || '$accent'}
                  backgroundColor={newTags.includes(tag) ? 'rgba(62, 207, 207, 0.1)' : '$sidebarActive'}
                >
                  <Text fontFamily="$fontBody" fontSize={11} color={tagColors[tag] || '$sidebarMuted'}>
                    {tag}
                  </Text>
                </Tag>
              ))}
            </View>
          </View>
          <SubmitButton onPress={handleCreate}>
            <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Create Post</Text>
          </SubmitButton>
        </CreateForm>
      )}

      <ScrollView flex={1} showsVerticalScrollIndicator={false}>
        {sortedPosts.map((post) => {
          const author = users[post.authorId]
          if (!author) return null
          return (
            <PostCard key={post.id}>
              <View flexDirection="row" alignItems="center" gap={8}>
                {post.isPinned && <Pin size={14} color="$accent" />}
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={15} color="$surfaceForeground" flex={1}>
                  {post.title}
                </Text>
              </View>

              <Text fontFamily="$fontMono" fontSize={13} color="$sidebarMuted" numberOfLines={2}>
                {post.content}
              </Text>

              <View flexDirection="row" alignItems="center" gap={8} flexWrap="wrap">
                {post.tags.map((tag) => (
                  <Tag key={tag}>
                    <Text fontFamily="$fontBody" fontSize={11} color={tagColors[tag] || '$sidebarMuted'}>
                      {tag}
                    </Text>
                  </Tag>
                ))}
              </View>

              <View flexDirection="row" alignItems="center" gap={10}>
                <Avatar name={author.displayName} color={author.roleColor || '#8B95A8'} size="xs" showStatus={false} />
                <Text fontFamily="$fontBody" fontSize={12} color={author.roleColor || '$sidebarMuted'}>
                  {author.displayName}
                </Text>
                <View flexDirection="row" alignItems="center" gap={4}>
                  <MessageSquare size={12} color="$color8" />
                  <Text fontFamily="$fontMono" fontSize={11} color="$color8">
                    {post.replyCount}
                  </Text>
                </View>
                <Text fontFamily="$fontMono" fontSize={11} color="$color8" marginLeft="auto">
                  {formatTimeAgo(post.lastActivityAt)}
                </Text>
              </View>
            </PostCard>
          )
        })}

        {sortedPosts.length === 0 && (
          <View flex={1} alignItems="center" justifyContent="center" padding={48} gap={12}>
            <MessageSquare size={32} color="$color8" />
            <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
              No posts yet. Start a discussion!
            </Text>
          </View>
        )}
      </ScrollView>
    </Container>
  )
}
